import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { apiGet } from '../../services/api';
import { KpiCard, SectionCard, fmt } from '../PainelDiarioUI';

/**
 * Configurações › Visão Geral: quem usa o sistema, e quando.
 *
 * Os números vêm de /system-stats/ (api/visao_geral.py): o dia de acesso de
 * cada pessoa — marcado em toda requisição, inclusive de quem segue logado
 * pelo cookie — somado aos logins. Cada cartão leva à lista Usuários e
 * acessos já filtrada; o quadro "Usuários e acessos" mostra um pedaço dela
 * aqui mesmo (quem está online, quem acessou hoje, quem parou, quem nunca
 * entrou).
 */

const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** Janela de "online agora", a mesma do servidor (api/visao_geral.py). */
export const MINUTOS_ONLINE = 10;
const ATUALIZA_ONLINE_MS = 60 * 1000;

/** Último acesso dentro da janela de online. */
export const estaOnline = (iso) => Boolean(iso)
  && Date.now() - new Date(iso).getTime() <= MINUTOS_ONLINE * 60000;

/** 'há 5 min', 'há 3 h', 'ontem', 'há 4 dias'. */
export function haQuanto(iso) {
  if (!iso) return 'nunca';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'ontem' : `há ${d} dias`;
}

/** As situações da lista Usuários e acessos (`?situacao=`). */
export const SITUACOES = [
  { value: '', label: 'Todas as contas' },
  { value: 'ativas', label: 'Contas ativas' },
  { value: 'online', label: `Online agora (últimos ${MINUTOS_ONLINE} min)` },
  { value: 'hoje', label: 'Acessaram hoje' },
  { value: '7d', label: 'Acessaram nos últimos 7 dias' },
  { value: '30d', label: 'Acessaram nos últimos 30 dias' },
  { value: 'sem30', label: 'Sem acesso há 30+ dias' },
  { value: 'nunca', label: 'Nunca acessaram' },
  { value: 'inativas', label: 'Contas inativas' },
];

/** A conta (uma linha de /system-stats/usuarios/) está na situação? */
export const naSituacao = (u, situacao) => {
  const d = u.dias_sem_acesso;
  switch (situacao) {
    case 'ativas': return u.is_active;
    case 'online': return u.is_active && estaOnline(u.ultimo_acesso);
    case 'hoje': return u.is_active && d === 0;
    case '7d': return u.is_active && d !== null && d <= 6;
    case '30d': return u.is_active && d !== null && d <= 29;
    case 'sem30': return u.is_active && d !== null && d > 29;
    case 'nunca': return u.is_active && d === null;
    case 'inativas': return !u.is_active;
    default: return true;
  }
};

const dataHora = (iso) => (iso
  ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : '');

const pctDe = (parte, total) => (total ? Math.round((parte / total) * 100) : 0);

/**
 * Quem está com o sistema aberto: último acesso nos últimos 10 minutos.
 * Toda tela logada consulta os avisos a cada 5 minutos, então quem está com
 * o SIDEB aberto aparece aqui — e quem fechou sai em até 10 minutos.
 * Atualiza sozinho a cada minuto.
 */
function useOnlineAgora(cidadeId) {
  const [online, setOnline] = useState(null);
  const buscar = useCallback(() => {
    apiGet('/system-stats/online/', cidadeId ? { city: cidadeId } : {})
      .then(setOnline)
      .catch(() => {});
  }, [cidadeId]);
  useEffect(() => {
    buscar();
    const id = setInterval(buscar, ATUALIZA_ONLINE_MS);
    return () => clearInterval(id);
  }, [buscar]);
  return [online, buscar];
}

const AZUL = '#0974BA';
const VERDE = '#13983D';

/** Bolinha verde que pulsa enquanto há alguém online. */
function PontoOnline({ ativo }) {
  return (
    <span className="relative inline-flex w-2 h-2 align-middle">
      {ativo && <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: VERDE }} />}
      <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: ativo ? VERDE : '#94a3b8' }} />
    </span>
  );
}

/**
 * Pessoas por dia, e na barra de hoje quem está online agora: o trecho
 * verde, embaixo, são os online; o azul, o resto de quem acessou hoje. Ao
 * passar o mouse na barra de hoje, os nomes de quem está online.
 *
 * Num mês que já acabou (`incluiHoje` falso) não há barra de hoje nem
 * online: a linha de cima resume o mês.
 */
function AcessosPorDia({ dias, online, darkMode, incluiHoje = true, rotuloPeriodo }) {
  const [foco, setFoco] = useState(null);
  const ultimo = dias.length - 1;
  const onlineTotal = incluiHoje ? (online?.total ?? 0) : 0;
  // O "online" se atualiza a cada minuto e o gráfico não: quem entrou agora
  // já está online e ainda não está na barra de hoje.
  const serie = dias.map((d, i) => (i === ultimo ? { ...d, usuarios: Math.max(d.usuarios, onlineTotal) } : d));
  const maior = Math.max(1, ...serie.map((d) => d.usuarios));
  const hoje = serie[ultimo];
  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const tinta = darkMode ? 'text-slate-400' : 'text-slate-500';
  const nomes = (online?.usuarios || []).map((u) => u.nome);
  const meio = dias[Math.floor(ultimo / 2)];
  const rotuloDia = (iso) => {
    const d = new Date(`${iso}T12:00:00`);
    return `${DIAS_SEMANA[d.getDay()]}, ${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  };

  let status;
  if (foco === null && !incluiHoje) {
    const pico = serie.reduce((a, d) => (d.usuarios > a.usuarios ? d : a), serie[0] || { usuarios: 0 });
    const media = serie.length ? serie.reduce((a, d) => a + d.usuarios, 0) / serie.length : 0;
    status = (
      <span>
        Em {rotuloPeriodo}: média de <strong className={forte}>{media.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}</strong> pessoa(s) por dia
        {pico?.usuarios > 0 && <>
          <span className="mx-1.5">·</span>
          pico de <strong className={forte}>{pico.usuarios}</strong> em {rotuloDia(pico.date)}
        </>}
      </span>
    );
  } else if (foco === null) {
    status = (
      <span>
        Hoje: <strong className={forte}>{hoje?.usuarios ?? 0}</strong> pessoa(s)
        <span className="mx-1.5">·</span>
        <PontoOnline ativo={onlineTotal > 0} />{' '}
        <strong className={forte}>{online ? onlineTotal : '—'}</strong> online agora
      </span>
    );
  } else if (foco === ultimo && incluiHoje) {
    status = (
      <span>
        <strong className={forte}>{hoje.usuarios}</strong> pessoa(s) hoje, <strong className={forte}>{onlineTotal}</strong> online agora
      </span>
    );
  } else {
    status = <span><strong className={forte}>{serie[foco].usuarios}</strong> pessoa(s) em {rotuloDia(serie[foco].date)}</span>;
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        {/* Uma linha só, cortada com "…": a lista de nomes não pode empurrar o gráfico. */}
        <div className={`min-w-0 flex-1 h-5 truncate text-xs ${tinta}`} aria-live="polite">{status}</div>
        <div className={`shrink-0 hidden sm:flex items-center gap-3 text-[11px] ${tinta}`}>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: AZUL }} />acessaram no dia</span>
          {incluiHoje && (
            <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: VERDE }} />online agora</span>
          )}
          {onlineTotal > 0 && (
            <Link to="/configuracoes/usuarios?situacao=online" className="font-medium text-[#0974BA] hover:underline">ver quem →</Link>
          )}
        </div>
      </div>
      <div className={`relative mt-3 h-56 flex items-end gap-[2px] border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}
        onMouseLeave={() => setFoco(null)}>
        {/* Linha do maior valor, recessiva */}
        <div className={`absolute inset-x-0 top-0 border-t border-dashed ${darkMode ? 'border-slate-700' : 'border-slate-200'}`} />
        {/* Na barra de hoje, o balão com quem está online */}
        {incluiHoje && foco === ultimo && nomes.length > 0 && (
          <div role="tooltip" style={{ right: `calc(${100 / serie.length}% + 6px)` }}
            className={`absolute top-2 z-10 w-72 rounded-lg px-3 py-2 text-xs shadow-lg pointer-events-none ${darkMode
              ? 'bg-slate-900 border border-slate-700 text-slate-200' : 'bg-white border border-slate-200 text-slate-700'}`}>
            <p className="mb-1 flex items-center gap-1.5 font-semibold">
              <PontoOnline ativo /> {onlineTotal} online agora
            </p>
            <ul className="space-y-0.5">
              {online.usuarios.slice(0, 8).map((u) => (
                <li key={u.id} className="truncate">
                  {u.nome} <span className={tinta}>· {u.role_display}</span>
                </li>
              ))}
            </ul>
            {nomes.length > 8 && <p className={`mt-1 ${tinta}`}>e mais {nomes.length - 8}</p>}
          </div>
        )}
        {serie.map((d, i) => {
          const ehHoje = incluiHoje && i === ultimo;
          const verdes = ehHoje ? Math.min(onlineTotal, d.usuarios) : 0;
          const azuis = d.usuarios - verdes;
          const opaco = foco === i ? 1 : 0.75;
          return (
            <button
              key={d.date}
              type="button"
              className="flex-1 h-full flex flex-col justify-end focus:outline-none"
              onMouseEnter={() => setFoco(i)}
              onFocus={() => setFoco(i)}
              onBlur={() => setFoco(null)}
              aria-label={ehHoje
                ? `Hoje: ${d.usuarios} pessoa(s), ${verdes} online agora`
                : `${rotuloDia(d.date)}: ${d.usuarios} pessoa(s)`}
            >
              {azuis > 0 && (
                <span className="w-full rounded-t-[4px] transition-opacity"
                  style={{ height: `${(azuis / maior) * 100}%`, minHeight: 2, background: AZUL, opacity: opaco }} />
              )}
              {verdes > 0 && (
                <span className={`w-full transition-opacity ${azuis > 0 ? 'mt-[2px]' : 'rounded-t-[4px]'}`}
                  style={{ height: `${(verdes / maior) * 100}%`, minHeight: 2, background: VERDE, opacity: foco === i ? 1 : 0.9 }} />
              )}
            </button>
          );
        })}
      </div>
      <div className={`mt-1 flex justify-between text-[11px] ${tinta}`}>
        <span>{dias[0] && `${dias[0].date.slice(8, 10)}/${dias[0].date.slice(5, 7)}`}</span>
        <span>{meio && `${meio.date.slice(8, 10)}/${meio.date.slice(5, 7)}`}</span>
        {incluiHoje ? (
          <span className="inline-flex items-center gap-1"><PontoOnline ativo={onlineTotal > 0} /> hoje</span>
        ) : (
          <span>{dias[ultimo] && `${dias[ultimo].date.slice(8, 10)}/${dias[ultimo].date.slice(5, 7)}`}</span>
        )}
      </div>
    </div>
  );
}

const rotuloMes = (mes) => {
  const [ano, m] = mes.split('-').map(Number);
  return new Date(ano, m - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
};

/** 'AAAA-MM' do mês atual até `primeiro` (inclusive), do mais recente ao mais antigo. */
function mesesDesde(primeiro) {
  const hoje = new Date();
  let ano = hoje.getFullYear();
  let mes = hoje.getMonth() + 1;
  const [anoIni, mesIni] = (primeiro || '').split('-').map(Number);
  const lista = [];
  // Um teto de 5 anos: o seletor não vira uma lista sem fim.
  while (lista.length < 60) {
    lista.push(`${ano}-${String(mes).padStart(2, '0')}`);
    if (!anoIni || ano < anoIni || (ano === anoIni && mes <= mesIni)) break;
    mes -= 1;
    if (mes === 0) { mes = 12; ano -= 1; }
  }
  return lista;
}

/**
 * O gráfico por dia de um mês (`mes` = 'AAAA-MM'); sem mês, null — o
 * gráfico usa os últimos 30 dias que já vêm na Visão Geral. Relê quando a
 * Visão Geral é atualizada (`versao`).
 */
function usePorDiaDoMes(mes, cidadeId, versao) {
  const [porDia, setPorDia] = useState(null);
  useEffect(() => {
    setPorDia(null);
    if (!mes) return undefined;
    let vivo = true;
    apiGet('/system-stats/por-dia/', { mes, ...(cidadeId ? { city: cidadeId } : {}) })
      .then((r) => vivo && setPorDia(r.por_dia || []))
      .catch(() => vivo && setPorDia([]));
    return () => { vivo = false; };
  }, [mes, cidadeId, versao]);
  return porDia;
}

function VisaoGeral({ stats, cidadeId, cidadeNome, darkMode, onAtualizar, atualizando }) {
  const navigate = useNavigate();
  const [online, buscarOnline] = useOnlineAgora(cidadeId);
  // Período do gráfico por dia: '' = últimos 30 dias; 'AAAA-MM' = um mês.
  const [mes, setMes] = useState('');
  const porDiaDoMes = usePorDiaDoMes(mes, cidadeId, stats?.gerado_em);
  const atualizar = () => {
    onAtualizar?.();
    buscarOnline();
  };
  if (!stats?.acessos) {
    return <p className="text-sm text-slate-500">Não foi possível carregar a visão geral.</p>;
  }
  const { contas, acessos, por_dia: porDia, por_perfil: porPerfil, por_municipio: porMunicipio,
    ultimos_acessos: ultimos, rede } = stats;
  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const suave = darkMode ? 'text-slate-400' : 'text-slate-500';
  const linha = darkMode ? 'border-slate-700/60' : 'border-slate-100';
  const trilho = darkMode ? 'bg-slate-700' : 'bg-slate-200';
  const ir = (situacao) => navigate(`/configuracoes/usuarios?situacao=${situacao}`);
  const meses = mesesDesde(stats.primeiro_mes);
  const mesAtual = meses[0];
  const seletorDoPeriodo = (
    <select
      value={mes}
      onChange={(e) => setMes(e.target.value)}
      aria-label="Período do gráfico"
      className={`px-2.5 py-1.5 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${darkMode
        ? 'bg-slate-700/50 border-slate-600 text-slate-200'
        : 'bg-white border-slate-200 text-slate-700'}`}
    >
      <option value="">Últimos 30 dias</option>
      {meses.map((m) => (
        <option key={m} value={m}>{rotuloMes(m)}</option>
      ))}
    </select>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className={`text-sm ${suave}`}>
          Uso do sistema — <span className={forte}>{cidadeNome || 'todos os municípios'}</span>
          {stats.gerado_em && <> · atualizado às {new Date(stats.gerado_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</>}
        </p>
        <div className="flex items-center gap-2">
          {onAtualizar && (
            <button type="button" onClick={atualizar} disabled={atualizando}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors disabled:opacity-60 disabled:cursor-wait ${darkMode
                ? 'border-slate-600 text-slate-200 hover:bg-slate-700/60'
                : 'border-slate-300 text-slate-700 hover:bg-slate-100'}`}>
              <RefreshCw className={`w-4 h-4 ${atualizando ? 'animate-spin' : ''}`} />
              {atualizando ? 'Atualizando…' : 'Atualizar'}
            </button>
          )}
          <Link to="/configuracoes/usuarios"
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25">
            Ver usuários e acessos →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiCard darkMode={darkMode} label="Contas ativas" value={fmt(contas.ativas)} tone="nav"
          foot={contas.inativas ? `${contas.inativas} inativa(s)` : 'nenhuma inativa'} onClick={() => ir('ativas')} />
        <KpiCard darkMode={darkMode} label="Acessaram hoje" value={fmt(acessos.hoje)} tone="good"
          foot={`${pctDe(acessos.hoje, contas.ativas)}% das contas`} onClick={() => ir('hoje')} />
        <KpiCard darkMode={darkMode} label="Últimos 7 dias" value={fmt(acessos.ultimos_7_dias)} tone="accent"
          foot={`${pctDe(acessos.ultimos_7_dias, contas.ativas)}% das contas`} onClick={() => ir('7d')} />
        <KpiCard darkMode={darkMode} label="Últimos 30 dias" value={fmt(acessos.ultimos_30_dias)} tone="accent"
          foot={`${pctDe(acessos.ultimos_30_dias, contas.ativas)}% das contas`} onClick={() => ir('30d')} />
        <KpiCard darkMode={darkMode} label="Sem acesso há 30+ dias" value={fmt(acessos.sem_acesso_30_dias)} tone="warn"
          foot="já entraram, pararam" onClick={() => ir('sem30')} />
        <KpiCard darkMode={darkMode} label="Nunca acessaram" value={fmt(acessos.nunca)} tone="crit"
          foot="conta criada, nenhum acesso" onClick={() => ir('nunca')} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <SectionCard darkMode={darkMode} className="xl:col-span-3" title="Pessoas que acessaram, por dia"
          action={seletorDoPeriodo}
          subtitle={`${mes ? `Dia a dia de ${rotuloMes(mes)}` : 'Últimos 30 dias'} · cada pessoa conta uma vez por dia${
            !mes || mes === mesAtual ? ` · online = sistema aberto nos últimos ${online?.janela_minutos || MINUTOS_ONLINE} min` : ''}`}>
          {mes && porDiaDoMes === null ? (
            <div className={`h-[17.5rem] flex items-center justify-center text-sm ${suave}`}>Carregando…</div>
          ) : (
            <AcessosPorDia
              dias={mes ? porDiaDoMes : porDia}
              online={online}
              darkMode={darkMode}
              incluiHoje={!mes || mes === mesAtual}
              rotuloPeriodo={mes ? rotuloMes(mes) : ''}
            />
          )}
        </SectionCard>

        <SectionCard darkMode={darkMode} className="xl:col-span-2" title="Por perfil"
          subtitle="Contas ativas e quantas acessaram nos últimos 30 dias">
          <ul className="space-y-3">
            {porPerfil.map((p) => {
              const pct = pctDe(p.acessaram_30d, p.total);
              return (
                <li key={p.role}>
                  <div className="flex justify-between gap-3 text-sm">
                    <span className={forte}>{p.role_display}</span>
                    <span className={`tabular-nums ${suave}`}>
                      <strong className={forte}>{p.acessaram_30d}</strong> de {p.total} · {pct}%
                    </span>
                  </div>
                  <div className={`mt-1 h-2 rounded-full ${trilho}`}>
                    <div className="h-2 rounded-full bg-[#0974BA]" style={{ width: `${pct}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SectionCard darkMode={darkMode} title="Últimos acessos" subtitle="Quem usou o sistema por último">
          {ultimos.length === 0 ? (
            <p className={`text-sm ${suave}`}>Nenhum acesso registrado ainda.</p>
          ) : (
            <ul>
              {ultimos.map((u) => (
                <li key={u.id} className={`py-2 flex items-center justify-between gap-3 border-b last:border-0 ${linha}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${darkMode ? 'bg-slate-700 text-[#5BB0E8]' : 'bg-[#0974BA]/10 text-[#0974BA]'}`}>
                      {u.nome.split(/\s+/).slice(0, 2).map((p) => p[0]).join('').toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${forte}`}>{u.nome}</p>
                      <p className={`text-xs truncate ${suave}`}>{u.role_display}{u.city ? ` · ${u.city}` : ''}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm ${forte}`}>{haQuanto(u.ultimo_acesso)}</p>
                    <p className={`text-[11px] ${suave}`}>{dataHora(u.ultimo_acesso)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        {porMunicipio.length > 1 ? (
          <SectionCard darkMode={darkMode} title="Por município" subtitle="Contas ativas e acessos">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${linha} text-[11px] uppercase tracking-wide ${suave}`}>
                  <th className="text-left py-2 font-semibold">Município</th>
                  <th className="text-right py-2 font-semibold">Contas</th>
                  <th className="text-right py-2 font-semibold">Hoje</th>
                  <th className="text-right py-2 font-semibold">30 dias</th>
                </tr>
              </thead>
              <tbody>
                {porMunicipio.map((m) => (
                  <tr key={m.city_id ?? 'sem'} className={`border-b last:border-0 ${linha}`}>
                    <td className={`py-2 ${forte}`}>{m.city}</td>
                    <td className={`py-2 text-right tabular-nums ${forte}`}>{m.total}</td>
                    <td className={`py-2 text-right tabular-nums ${forte}`}>{m.hoje}</td>
                    <td className={`py-2 text-right tabular-nums ${forte}`}>
                      {m.acessaram_30d} <span className={`text-[11px] ${suave}`}>({pctDe(m.acessaram_30d, m.total)}%)</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>
        ) : (
          // Um município só: a base de dados ocupa pouco, e o resto da
          // coluna fica com a lista de usuários e acessos.
          <div className="flex flex-col gap-6 min-w-0">
            <SectionCard darkMode={darkMode} title="Base de dados" subtitle={cidadeNome ? `Cadastros de ${cidadeNome}` : 'Cadastros da rede'}>
              <BaseDeDados rede={rede} darkMode={darkMode} />
            </SectionCard>
            <SectionCard darkMode={darkMode} className="flex-1" title="Usuários e acessos"
              subtitle="Quem está usando, quem parou e quem nunca entrou">
              <UsuariosEAcessos cidadeId={cidadeId} darkMode={darkMode} versao={stats.gerado_em} />
            </SectionCard>
          </div>
        )}
      </div>

      {porMunicipio.length > 1 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <SectionCard darkMode={darkMode} title="Base de dados" subtitle="Cadastros da rede">
            <BaseDeDados rede={rede} darkMode={darkMode} />
          </SectionCard>
          <SectionCard darkMode={darkMode} title="Usuários e acessos"
            subtitle="Quem está usando, quem parou e quem nunca entrou">
            <UsuariosEAcessos cidadeId={cidadeId} darkMode={darkMode} versao={stats.gerado_em} />
          </SectionCard>
        </div>
      )}
    </div>
  );
}

/** Os filtros do quadro, na ordem em que interessam a quem acompanha a rede. */
const FILTROS_DO_QUADRO = [
  { value: 'online', label: 'Online agora' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'sem30', label: 'Sem acesso 30+ dias' },
  { value: 'nunca', label: 'Nunca acessaram' },
];
const LINHAS_NO_QUADRO = 5;

/**
 * Um pedaço da lista Usuários e acessos: escolhe-se a situação e aparecem as
 * primeiras contas nela, com o link para a lista inteira já filtrada.
 * O "online" é calculado aqui, pelo último acesso: atualiza a cada minuto
 * sem precisar buscar a lista de novo.
 */
function UsuariosEAcessos({ cidadeId, darkMode, versao }) {
  const [usuarios, setUsuarios] = useState(null);
  const [erro, setErro] = useState(false);
  const [situacao, setSituacao] = useState('online');
  const [, setRelogio] = useState(0);

  useEffect(() => {
    let vivo = true;
    setErro(false);
    apiGet('/system-stats/usuarios/', cidadeId ? { city: cidadeId } : {})
      .then((r) => vivo && setUsuarios(r?.usuarios || []))
      .catch(() => vivo && setErro(true));
    return () => { vivo = false; };
  }, [cidadeId, versao]);

  useEffect(() => {
    const id = setInterval(() => setRelogio((n) => n + 1), ATUALIZA_ONLINE_MS);
    return () => clearInterval(id);
  }, []);

  const forte = darkMode ? 'text-slate-100' : 'text-slate-800';
  const suave = darkMode ? 'text-slate-400' : 'text-slate-500';
  const linha = darkMode ? 'border-slate-700/60' : 'border-slate-100';

  if (erro) return <p className={`text-sm ${suave}`}>Não foi possível carregar os usuários.</p>;
  if (!usuarios) return <p className={`text-sm ${suave}`}>Carregando…</p>;

  const contagem = Object.fromEntries(
    FILTROS_DO_QUADRO.map((f) => [f.value, usuarios.filter((u) => naSituacao(u, f.value)).length]),
  );
  const lista = usuarios
    .filter((u) => naSituacao(u, situacao))
    .sort(situacao === 'nunca'
      ? (a, b) => a.nome.localeCompare(b.nome, 'pt-BR')
      : (a, b) => (b.ultimo_acesso || '').localeCompare(a.ultimo_acesso || ''));
  const visiveis = lista.slice(0, LINHAS_NO_QUADRO);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3" role="tablist" aria-label="Situação">
        {FILTROS_DO_QUADRO.map((f) => {
          const ativo = situacao === f.value;
          return (
            <button key={f.value} type="button" role="tab" aria-selected={ativo}
              onClick={() => setSituacao(f.value)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${ativo
                ? 'text-white border-transparent bg-[#0974BA]'
                : darkMode
                  ? 'border-slate-600 text-slate-300 hover:bg-slate-700/60'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
              {f.value === 'online' && <PontoOnline ativo={contagem.online > 0} />}
              {f.label}
              <span className={`tabular-nums ${ativo ? 'text-white/80' : suave}`}>{contagem[f.value]}</span>
            </button>
          );
        })}
      </div>

      {visiveis.length === 0 ? (
        <p className={`py-6 text-center text-sm ${suave}`}>Ninguém nesta situação.</p>
      ) : (
        <ul>
          {visiveis.map((u) => {
            const online = estaOnline(u.ultimo_acesso);
            return (
              <li key={u.id} className={`py-2 flex items-center justify-between gap-3 border-b last:border-0 ${linha}`}>
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${forte}`}>{u.nome}</p>
                  <p className={`text-xs truncate ${suave}`} title={u.escolas.join(', ')}>
                    {u.role_display}
                    {u.escolas.length > 0 && ` · ${u.escolas[0]}${u.escolas.length > 1 ? ` +${u.escolas.length - 1}` : ''}`}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {u.ultimo_acesso ? (
                    <>
                      <p className={`text-sm inline-flex items-center gap-1.5 ${forte}`}>
                        {online && <span className="w-2 h-2 rounded-full" style={{ background: VERDE }} />}
                        {online ? 'online' : haQuanto(u.ultimo_acesso)}
                      </p>
                      <p className={`text-[11px] ${suave}`}>{u.dias_com_acesso_30d} dia(s) em 30</p>
                    </>
                  ) : (
                    <p className={`text-sm ${suave}`}>nunca</p>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Link to={`/configuracoes/usuarios?situacao=${situacao}`}
        className="mt-3 inline-block text-sm font-medium text-[#0974BA] hover:underline">
        {lista.length > visiveis.length ? `Ver todos os ${fmt(lista.length)} →` : 'Abrir Usuários e acessos →'}
      </Link>
    </div>
  );
}

function BaseDeDados({ rede, darkMode }) {
  const itens = [
    ['Escolas', rede.escolas], ['Alunos', rede.alunos], ['Professores', rede.professores],
    ['Turmas', rede.turmas], ['Provas', rede.provas],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {itens.map(([rotulo, valor]) => (
        <div key={rotulo} className={`rounded-xl px-3 py-2 ${darkMode ? 'bg-slate-700/30' : 'bg-slate-50'}`}>
          <p className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{rotulo}</p>
          <p className={`text-lg font-semibold tabular-nums ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{fmt(valor)}</p>
        </div>
      ))}
    </div>
  );
}

export default VisaoGeral;
